import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { EmptyState } from '../../components/EmptyState';
import { QrImage } from '../../components/QrImage';
import { LIST_MAX_HEIGHT, Table } from '../../components/Table';
import { RefreshIcon, TrashIcon } from '../../components/icons';
import { BulkActionModal, type BulkSubmit } from './BulkActionModal';
import { BulkResult, type BulkResultItem } from './BulkResult';
import type { BulkResponse } from './types';
import type { BulkAction } from './bulkActions';
import { describeApiError } from '../qrs/errors';
import { itemsOf, queryString, useApiQuery, useDebounced } from '../qrs/hooks';
import { STATE_LABEL, STATE_ORDER } from '../qrs/states';
import { operatorQrImagePath } from '../qrs/utils';
import { QrOperatorDetail } from './QrOperatorDetail';
import {
  QR_TYPE_LABEL,
  QR_TYPE_TONE,
  stateChipFor,
  type Domain,
  type OperatorQrDetail,
  type OperatorQrItem,
  type Org,
  type QrImageType,
} from './types';

interface Filters {
  orgId: string;
  domain: string;
  qrType: QrImageType | '';
  state: string;
  q: string;
}

const EMPTY: Filters = { orgId: '', domain: '', qrType: '', state: '', q: '' };
const TYPES: (QrImageType | '')[] = ['', 'classic', 'styled', 'art'];
const PAGE = 50;

function shortTarget(url: string | null): string {
  return url ? url.replace(/^https?:\/\//, '').replace(/\/$/, '') : '—';
}

/** Operador → QR (R14.2): los links de todas las organizaciones con su QR real. */
export function QrsOperatorPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('id');
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const q = useDebounced(filters.q, 300);
  const [items, setItems] = useState<OperatorQrItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [allFromFilter, setAllFromFilter] = useState(false);
  const [bulk, setBulk] = useState<BulkAction | null>(null);
  const [result, setResult] = useState<{ action: BulkAction; items: BulkResultItem[]; running: boolean; total: number } | null>(null);

  const orgs = useApiQuery((signal) => api.get<{ items: Org[] }>('/v1/orgs', { signal }).then(itemsOf).catch(() => [] as Org[]), []);
  const domains = useApiQuery((signal) => api.get<{ items: Domain[] }>('/v1/domains', { signal }).then(itemsOf).catch(() => [] as Domain[]), []);

  const listPath = useCallback(
    (after: string | null) =>
      `/v1/operator/qrs${queryString({ orgId: filters.orgId, domain: filters.domain, qrType: filters.qrType, state: filters.state, q: q.trim(), limit: PAGE, cursor: after })}`,
    [filters.orgId, filters.domain, filters.qrType, filters.state, q],
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<{ items: OperatorQrItem[]; nextCursor?: string | null }>(listPath(null), { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setItems(res.items ?? []);
        setCursor(res.nextCursor ?? null);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [listPath, tick]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await api.get<{ items: OperatorQrItem[]; nextCursor?: string | null }>(listPath(cursor));
      setItems((prev) => [...prev, ...(res.items ?? [])]);
      setCursor(res.nextCursor ?? null);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }

  const detail = useApiQuery(
    (signal) => (selectedId ? api.get<OperatorQrDetail>(`/v1/operator/qrs/${encodeURIComponent(selectedId)}`, { signal }) : Promise.resolve(undefined)),
    [selectedId],
  );

  function select(id: string) {
    const next = new URLSearchParams(params);
    next.set('id', id);
    setParams(next, { replace: true });
  }

  const byId = useMemo(() => new Map(items.map((r) => [r.id, r])), [items]);
  const selectedCodes = (allFromFilter ? items : items.filter((r) => selected.includes(r.id))).map((r) => r.code);

  /** Acción masiva (R3.2–R3.4): acá vive la única llamada al lote y el seguimiento de su resultado. */
  async function runBulk(input: BulkSubmit) {
    const res = await api.post<BulkResponse>('/v1/operator/qrs/bulk', {
      action: input.action,
      codes: input.codes,
      reason: input.reason || undefined,
      prompt: input.prompt,
      confirmCount: input.codes.length >= 10 ? input.codes.length : undefined,
    });
    setResult({ action: input.action, items: res.items ?? [], running: res.status !== 'listo', total: input.codes.length });
    setSelected([]);
    setAllFromFilter(false);
    // Cuando el lote corre en segundo plano, la consola sigue usable y el resultado va llegando solo.
    if (res.status !== 'listo' && res.bulkId) void followBulk(res.bulkId, input.action, input.codes.length);
  }

  async function followBulk(bulkId: string, action: BulkSubmit['action'], total: number) {
    for (let intento = 0; intento < 40; intento += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const res = await api.get<BulkResponse>(`/v1/operator/bulk/${encodeURIComponent(bulkId)}`);
        setResult({ action, items: res.items ?? [], running: res.status !== 'listo', total });
        if (res.status === 'listo') return;
      } catch {
        return; // si la consulta falla, el resultado queda como estaba en vez de borrarse
      }
    }
  }

  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => setFilters((f) => ({ ...f, [key]: value }));
  const filtered = Boolean(filters.orgId || filters.domain || filters.qrType || filters.state || filters.q);

  return (
    <section className="space-y-5">
      <div>
        <h1 className="font-display text-xl font-bold text-ink">QR de todas las organizaciones</h1>
        <p className="text-sm text-ink-muted">Cada link con su QR real: Clásico, Con estilo o QR Art. Elige una fila para ver el QR grande y descargarlo.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-white p-3" role="group" aria-label="Filtros">
        <label htmlFor="opqr-org" className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Organización
          <select id="opqr-org" className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink" value={filters.orgId} onChange={(e) => set('orgId', e.target.value)}>
            <option value="">Todas</option>
            {(orgs.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name ?? o.id}</option>
            ))}
          </select>
        </label>
        <label htmlFor="opqr-domain" className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Dominio
          <select id="opqr-domain" className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink" value={filters.domain} onChange={(e) => set('domain', e.target.value)}>
            <option value="">Todos</option>
            {(domains.data ?? []).map((d) => (
              <option key={d.name} value={d.name}>{d.name}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          <span id="opqr-type-label">Tipo</span>
          <div className="inline-flex rounded-md border border-line bg-mist p-0.5" role="group" aria-labelledby="opqr-type-label">
            {TYPES.map((t) => (
              <button
                key={t || 'all'}
                type="button"
                aria-pressed={filters.qrType === t}
                onClick={() => set('qrType', t)}
                className={`rounded-sm px-3 py-1.5 text-sm ${filters.qrType === t ? 'bg-white font-semibold text-ink shadow-1' : 'text-ink-muted'}`}
              >
                {t ? QR_TYPE_LABEL[t] : 'Todos'}
              </button>
            ))}
          </div>
        </div>
        <label htmlFor="opqr-state" className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Estado
          <select id="opqr-state" className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink" value={filters.state} onChange={(e) => set('state', e.target.value)}>
            <option value="">Todos</option>
            {STATE_ORDER.map((s) => (
              <option key={s} value={s}>{STATE_LABEL[s]}</option>
            ))}
          </select>
        </label>
        <label htmlFor="opqr-q" className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
          Buscar
          <input
            id="opqr-q"
            type="search"
            placeholder="Código o referencia"
            className="rounded-sm border border-line bg-white px-2 py-2 text-sm text-ink"
            value={filters.q}
            onChange={(e) => set('q', e.target.value)}
          />
        </label>
        <span className="ml-auto self-center text-sm tabular-nums text-ink-muted" aria-live="polite">
          {loading ? 'Cargando…' : `${items.length}${cursor ? '+' : ''} links`}
        </span>
      </div>

      {error ? (
        <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={() => setTick((t) => t + 1)}>Reintentar</Button>}>
          {describeApiError(error, 'No pudimos cargar los QR.')}
        </Banner>
      ) : null}

      {selectedCodes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-white p-3" role="toolbar" aria-label="Acciones sobre la selección">
          <span className="text-sm font-semibold tabular-nums text-ink">
            {selectedCodes.length === 1 ? '1 QR seleccionado' : `${selectedCodes.length} QR seleccionados`}
          </span>
          <Button size="sm" variant="secondary" onClick={() => setBulk('regen_art')}>
            <RefreshIcon />
            Regenerar QR Art
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setBulk('regen_prompt')}>Nueva versión con otro prompt</Button>
          <Button size="sm" variant="secondary" onClick={() => setBulk('release_pool')}>Liberar al pool</Button>
          <Button size="sm" variant="secondary" onClick={() => setBulk('retire')}>Dar de baja</Button>
          <Button size="sm" variant="secondary" onClick={() => setBulk('unlink')}>Quitar código</Button>
          <Button size="sm" variant="danger" onClick={() => setBulk('delete')}>
            <TrashIcon />
            Eliminar
          </Button>
        </div>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 xl:grid-cols-[minmax(0,1fr),minmax(20rem,26rem)]">
        <div className="space-y-3">
          <Table<OperatorQrItem>
            caption="Links de todas las organizaciones"
            rows={items}
            rowKey={(r) => r.id}
            loading={loading}
            stickyHeader
            maxHeight={LIST_MAX_HEIGHT}
            empty={
              <EmptyState
                title={filtered ? 'Ningún link calza con los filtros' : 'Todavía no hay links'}
                description={filtered ? 'Prueba con otro filtro o busca por código.' : 'Cuando las organizaciones creen o activen QR aparecerán aquí.'}
              />
            }
            selection={{
              selected,
              onChange: setSelected,
              label: (id) => `Seleccionar ${byId.get(id)?.code ?? id}`,
              filter: !cursor && items.length > 0 ? { total: items.length, selected: allFromFilter, onChange: setAllFromFilter } : undefined,
            }}
            columns={[
              { key: 'thumb', header: 'QR', render: (r) => <QrImage lazy className="h-12 w-12" path={operatorQrImagePath(r.id, { size: 256 })} alt={`QR ${r.code}`} /> },
              {
                key: 'code',
                header: 'Código',
                render: (r) => (
                  <button type="button" onClick={() => select(r.id)} className="text-left" aria-current={r.id === selectedId || undefined}>
                    <span className="font-mono font-semibold tracking-wider text-ink underline">{r.code}</span>
                    <span className="block text-xs text-ink-muted">{r.domain}</span>
                  </button>
                ),
              },
              { key: 'org', header: 'Organización', render: (r) => r.orgName ?? r.orgId ?? <span className="text-ink-muted">Sin asignar</span> },
              { key: 'type', header: 'Tipo', render: (r) => <Chip tone={QR_TYPE_TONE[r.qrType]} dot={false}>{QR_TYPE_LABEL[r.qrType]}</Chip> },
              {
                key: 'state',
                header: 'Estado',
                render: (r) => {
                  const state = stateChipFor(r.state);
                  return <Chip tone={state.tone}>{state.label}</Chip>;
                },
              },
              {
                key: 'target',
                header: 'Destino',
                render: (r) => (
                  <span className="block max-w-[14rem] truncate text-ink-muted" title={r.targetUrl ?? undefined}>
                    {shortTarget(r.targetUrl)}
                  </span>
                ),
              },
              {
                key: 'audit',
                header: '',
                align: 'right',
                render: (r) => (
                  <Link to={`/operador/qr/${encodeURIComponent(r.code)}/auditoria`} className="text-green underline" aria-label={`Auditoría de ${r.code}`}>
                    Auditoría
                  </Link>
                ),
              },
            ]}
          />
          {result && <BulkResult action={result.action} items={result.items} running={result.running} total={result.total} />}
          {cursor && !loading && (
            <div className="flex justify-center">
              <Button variant="secondary" loading={loadingMore} onClick={() => void loadMore()}>Cargar más</Button>
            </div>
          )}
        </div>

        <aside className="rounded-lg border border-line bg-white p-4 xl:sticky xl:top-4" aria-live="polite" aria-label="Ficha del QR">
          {!selectedId ? (
            <p className="text-sm text-ink-muted">Elige un link de la tabla para ver su QR, descargarlo y revisar su historial.</p>
          ) : detail.error ? (
            <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={detail.reload}>Reintentar</Button>}>
              {describeApiError(detail.error, 'No pudimos cargar la ficha del QR.')}
            </Banner>
          ) : detail.loading || !detail.data ? (
            <div role="status" className="text-sm text-ink-muted">Cargando ficha…</div>
          ) : (
            <QrOperatorDetail qr={detail.data} />
          )}
        </aside>
      </div>

      {bulk && <BulkActionModal action={bulk} codes={selectedCodes} onClose={() => setBulk(null)} onSubmit={runBulk} />}
    </section>
  );
}
